/**
 * ScrubBar — shared draggable progress bar for AudioPlayer and VideoPlayer.
 *
 * Features:
 *   - Click-to-seek on the track
 *   - Draggable thumb (pointer-event based — works on touch + mouse)
 *   - 44px touch target with 4px visual track (Apple HIG)
 *   - Time tooltip above thumb while dragging
 *   - Customisable colors via `colors` prop
 */

import React, { useRef, useState, useCallback } from 'react';

function formatTime(seconds) {
  if (!isFinite(seconds) || isNaN(seconds)) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export default function ScrubBar({
  progress = 0,
  duration = 0,
  currentTime = 0,
  onSeek,
  colors = {},
}) {
  const trackRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [hoverProgress, setHoverProgress] = useState(null);

  const trackBg = colors.trackBg || 'bg-slate-200 dark:bg-[#3e4145]';
  const fillBg = colors.fillBg || 'bg-blue-500';
  const thumbBg = colors.thumbBg || 'bg-white';
  const tooltipBg = colors.tooltipBg || 'bg-slate-800';
  const tooltipText = colors.tooltipText || 'text-white';

  const clampedProgress = Math.max(0, Math.min(100, progress));
  const displayProgress = isDragging && hoverProgress !== null ? hoverProgress : clampedProgress;

  const getProgressFromEvent = useCallback((e) => {
    if (!trackRef.current) return 0;
    const rect = trackRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    return Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
  }, []);

  const commitSeek = useCallback((pct) => {
    if (onSeek && duration > 0) {
      onSeek((pct / 100) * duration);
    }
  }, [onSeek, duration]);

  const handlePointerDown = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
    const pct = getProgressFromEvent(e);
    setHoverProgress(pct);
    commitSeek(pct);

    const onMove = (ev) => {
      const p = getProgressFromEvent(ev);
      setHoverProgress(p);
      commitSeek(p);
    };
    const onUp = () => {
      setIsDragging(false);
      setHoverProgress(null);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }, [getProgressFromEvent, commitSeek]);

  return (
    <div
      ref={trackRef}
      className="relative w-full h-11 flex items-center cursor-pointer touch-none select-none"
      onPointerDown={handlePointerDown}
      data-testid="scrub-bar"
    >
      {/* Visual track */}
      <div className={`absolute inset-x-0 h-1 rounded-full ${trackBg}`} style={{ top: '50%', transform: 'translateY(-50%)' }}>
        {/* Fill */}
        <div
          className={`absolute inset-y-0 left-0 rounded-full ${fillBg} transition-[width] duration-75`}
          style={{ width: `${displayProgress}%` }}
        />
      </div>

      {/* Thumb */}
      <div
        className={`absolute w-3.5 h-3.5 rounded-full ${thumbBg} shadow-md border-2 border-white dark:border-slate-700 transition-transform`}
        style={{ left: `calc(${displayProgress}% - 7px)`, top: '50%', transform: `translateY(-50%)${isDragging ? ' scale(1.25)' : ''}` }}
      />

      {/* Tooltip while dragging */}
      {isDragging && hoverProgress !== null && (
        <div
          className={`absolute -top-7 px-1.5 py-0.5 rounded text-[10px] font-medium ${tooltipBg} ${tooltipText} pointer-events-none whitespace-nowrap`}
          style={{ left: `calc(${hoverProgress}% - 16px)` }}
        >
          {formatTime((hoverProgress / 100) * duration)}
        </div>
      )}
    </div>
  );
}
